/**
 * use-last-exchange.ts
 *
 * Charge en batch le dernier message echange pour chaque contact d'une team.
 * Utilise par la table CRM pour afficher la colonne "Dernier echange".
 *
 * Strategie : 1 requete conversations + 1 requete messages (limit 1000 desc).
 * Cote client, on dedupe par contact_id en gardant le message le plus recent.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTeamId } from "@/hooks/use-team-id";

export interface LastExchange {
  contactId: string;
  content: string;
  sentAt: string;
  direction: "in" | "out";
  senderType: "ai" | "human" | string;
  channel: string;
}

export function useLastExchange() {
  const { teamId } = useTeamId();

  return useQuery({
    queryKey: ["contact-last-exchange", teamId],
    queryFn: async (): Promise<Map<string, LastExchange>> => {
      // 1. Conversations de la team avec contact lie
      const { data: convs, error: convError } = await supabase
        .from("conversations")
        .select("id, contact_id, channel")
        .eq("team_id", teamId!)
        .not("contact_id", "is", null);

      if (convError) throw convError;
      if (!convs || convs.length === 0) return new Map();

      const convInfo = new Map<string, { contactId: string; channel: string }>();
      for (const c of convs) {
        if (c.contact_id) {
          convInfo.set(c.id, { contactId: c.contact_id, channel: c.channel });
        }
      }

      // 2. Messages les plus recents (toutes conversations confondues)
      const convIds = Array.from(convInfo.keys());
      const { data: msgs, error: msgError } = await supabase
        .from("messages")
        .select("conversation_id, content, sent_at, direction, sender_type")
        .in("conversation_id", convIds)
        .order("sent_at", { ascending: false })
        .limit(1500);

      if (msgError) throw msgError;

      // 3. Dedupe par contactId : on garde le premier rencontre (= le plus recent)
      const map = new Map<string, LastExchange>();
      for (const m of msgs ?? []) {
        const meta = convInfo.get(m.conversation_id);
        if (!meta) continue;
        if (map.has(meta.contactId)) continue;
        if (!m.content) continue;
        map.set(meta.contactId, {
          contactId: meta.contactId,
          content: m.content,
          sentAt: m.sent_at,
          direction: (m.direction as LastExchange["direction"]) ?? "out",
          senderType: m.sender_type ?? "human",
          channel: meta.channel,
        });
      }
      return map;
    },
    enabled: !!teamId,
    staleTime: 60 * 1000,
  });
}
